import { z } from "zod";

import { errorMessage, i18n } from "@/i18n";
import { isPrefecture } from "@/types/prefectures";
import { encodeImageToBase64 } from "@/util";

// Zod 4.0のカスタムエラーマップ実装
z.config({
  customError: (iss) => {
    if (iss.code === "invalid_type") {
      if (iss.input === undefined) {
        if (
          iss.path &&
          (iss.path.includes("date") || iss.path.includes("workDate"))
        ) {
          return "日付を選択してください。";
        }
        return "必須入力です。";
      }
    }
    if (iss.code === "custom") {
      if (
        iss.path &&
        (iss.path.includes("date") || iss.path.includes("workDate"))
      ) {
        return "日付の形式ではありません。";
      }
    }

    // デフォルトエラーメッセージにフォールバック
    return undefined;
  },
});

// 正規表現パターン
const alphanumericPattern = /^[\u0021-\u007e]+$/u; // 半角英数字記号のみ
const fullwidthCaracterPattern = /^[^\x01-\x7E\xA1-\xDF]+$/; // 全角のみ
const namePattern =
  /^[a-zA-Zａ-ｚＡ-Ｚぁ-んァ-ヶー一-龠々〆〤\s\-'\u30B8・･]+$/; // 日本語、英語、ハイフン、アポストロフィ、中点を許可
const specialSymbolPattern = /[^!@#$%^&*()_+{}[\]:;<>,.?~]/; // 特殊記号除外
const kanaPattern = /^[ァ-ヶー]*$/; // カナのみ
// 氏名が日本人の一般的な表記かどうかの判定に使う。namePattern と同じ漢字の範囲を使う
const kanjiOnlyPattern = /^[一-龠々〆〤]+$/; // 漢字のみ（姓）
const kanjiKanaOnlyPattern = /^[一-龠々〆〤ぁ-んァ-ヶー]+$/; // 漢字・ひらがな・カタカナのみ（名）
// const phonePattern = /^0\d{9,10}$/; // 国内電話番号
// const zipCodePattern = /^\d{7}$/; // 国内郵便番号
const sevenDigitPattern = /^\d{7}$/; // 7桁の半角数字
// const streetPattern = /^.*?[0-9０-９]+.*?[-－ー−].*?$/; // 番地入力を許容
// const buildingNamePattern = /^[a-zA-Z0-9０-９ぁ-んァ-ン一-龠々〆〤ー－－\s]+$/; // 建物入力を許容

// 必須入力バリデーション
const requiredValidation = (label: string) =>
  z
    .string({
      error: (issue) =>
        issue.input === undefined
          ? `${label}を入力してください。`
          : `${label}は文字列である必要があります。`,
    })
    .trim()
    .min(1, { error: `${label}を入力してください。` });

export const emailValidation = () =>
  requiredValidation(i18n("email"))
    .email({ message: `${i18n("email")}の形式ではありません。` })
    .regex(alphanumericPattern, {
      message: errorMessage("onlyAlphanumeric"),
    });

export const lastNameValidation = () =>
  requiredValidation(i18n("lastName")).regex(namePattern, {
    message: errorMessage("invalidNameFormat"),
  });

export const firstNameValidation = () =>
  requiredValidation(i18n("firstName")).regex(namePattern, {
    message: errorMessage("invalidNameFormat"),
  });

export const firstNameKanaValidation = () =>
  requiredValidation(i18n("firstNameKana"))
    .regex(kanaPattern, {
      message: errorMessage("onlyKana"),
    })
    .regex(fullwidthCaracterPattern, {
      message: errorMessage("onlyFullwidthCaracters"),
    });

export const lastNameKanaValidation = () =>
  requiredValidation(i18n("lastNameKana"))
    .regex(kanaPattern, {
      message: errorMessage("onlyKana"),
    })
    .regex(fullwidthCaracterPattern, {
      message: errorMessage("onlyFullwidthCaracters"),
    });

export const passwordValidation = () =>
  requiredValidation(i18n("password")).min(
    8,
    "パスワードは8文字以上で入力してください",
  );

// 電話番号ハイフンあり
export const phoneNumberValidation = () =>
  requiredValidation(i18n("phoneNumber")).regex(
    /^0[9876]0[-]?\d{4}[-]?\d{4}$/,
    "090、080、070、060で始まる11桁の半角数字を入力してください。",
  );

export const smsCodeValidation = () =>
  requiredValidation(i18n("smsCode")).regex(
    /^(\d{4}|\d{6})$/,
    "4桁の半角数字を入力してください。",
  );

export const sexValidation = () =>
  z.union([z.literal("female"), z.literal("male")], {
    error: (issue) => {
      if (issue.code === "invalid_union") {
        return {
          message: "男性もしくは女性を選択してください。",
        };
      }
      return {
        message: issue.message ?? "男性もしくは女性を選択してください。",
      };
    },
  });

export const hasDriversLicenseValidation = () =>
  z.union([z.literal("true"), z.literal("false")], {
    error: (issue) => {
      if (issue.code === "invalid_union") {
        return {
          message: "【あり】もしくは【なし】を選択してください。",
        };
      }
      return {
        message:
          issue.message ?? "【あり】もしくは【なし】を選択してください。",
      };
    },
  });

export const experienceYearsValidation = () =>
  z.union([
    z.literal("less_one_year"),
    z.literal("one_to_three_year"),
    z.literal("three_to_five_year"),
    z.literal("over_five_year"),
  ]);

// 経験年数を選んだ場合は、経験がある業務内容が1件以上必要（サーバー側の判定に合わせる）
export const isExperienceWorkMissing = (data: {
  experienceYears?: string;
  experienceWork?: string[];
}) => !!data.experienceYears && (data.experienceWork?.length ?? 0) === 0;

export const JAPAN_NATIONALITY = "日本";

export const NATIONALITY_TYPE_JAPAN = "japan";
export const NATIONALITY_TYPE_FOREIGN = "foreign";

export const nationalityTypeValidation = () =>
  z.enum([NATIONALITY_TYPE_JAPAN, NATIONALITY_TYPE_FOREIGN]);

// 選択式なので「入力してください」ではなく「選択してください」を出す
export const nationalityValidation = () =>
  z
    .string({ error: `${i18n("nationality")}を選択してください。` })
    .trim()
    .min(1, { error: `${i18n("nationality")}を選択してください。` });

/**
 * 氏名が日本人の一般的な表記と異なるか。
 * 姓は漢字のみ、名は漢字・ひらがな・カタカナのみを一般的な表記とみなす。
 * 氏名カナ欄は全角カタカナへ正規化されるため国籍による差異が現れず、判定には使わない。
 *
 * 漢字表記が成立する国籍（中国・台湾・韓国など）は文字種では区別できないため検知できない。
 * 国籍は自己申告であり、この判定は保存前の注意喚起にのみ使う（保存は拒否しない）。
 */
export const isUncommonJapaneseName = (data: {
  lastName?: string;
  firstName?: string;
}) => {
  const lastName = data.lastName?.trim() ?? "";
  const firstName = data.firstName?.trim() ?? "";
  // 未入力は各項目の必須チェックに任せ、ここでは判定しない
  if (!lastName && !firstName) return false;

  const lastNameIsUncommon = !!lastName && !kanjiOnlyPattern.test(lastName);
  const firstNameIsUncommon =
    !!firstName && !kanjiKanaOnlyPattern.test(firstName);

  return lastNameIsUncommon || firstNameIsUncommon;
};
// 経験がある業務内容を選んだ場合は、経験年数も必要（サーバー側の判定に合わせる）
export const isExperienceYearsMissing = (data: {
  experienceYears?: string;
  experienceWork?: string[];
}) => (data.experienceWork?.length ?? 0) > 0 && !data.experienceYears;

// 勤務経験のある施設を選んだ場合も、経験年数が必要（サーバー側の判定に合わせる）
export const isExperienceFacilitiesYearsMissing = (data: {
  experienceYears?: string;
  experienceFacilities?: string[];
}) => (data.experienceFacilities?.length ?? 0) > 0 && !data.experienceYears;

export const zipCodeValidation = () =>
  requiredValidation(i18n("zipCode")).regex(sevenDigitPattern, {
    message: "7桁の半角数字を入力してください。",
  });

export const prefectureValidation = () =>
  requiredValidation(i18n("prefecture")).refine(
    (value) => isPrefecture(value),
    "47都道府県のいずれかを入力してください。",
  );

export const cityValidation = () =>
  z
    .string({
      message: `${i18n("city")}を選択してください。`,
    })
    .regex(fullwidthCaracterPattern, {
      message: errorMessage("onlyFullwidthCaracters"),
    });

export const streetValidation = () =>
  requiredValidation(i18n("street")).regex(specialSymbolPattern, {
    message: errorMessage("notStreet"),
  });

export const buildingValidation = () =>
  z
    .string()
    .regex(specialSymbolPattern, {
      message: errorMessage("notBuilding"),
    })
    .or(z.string().length(0));

export const messageValidation = () =>
  z
    .object({
      body: z.string(),
      image: messageImageValidation(),
      attachment: messageAttachmentValidation(),
      originalFileName: z.string(),
    })
    .refine((data) => !!data.body || !!data.image || !!data.attachment, {
      path: ["oneOfTheInputsIsRequired"],
      message: "メッセージ、画像、またはPDFを入力してください",
    });

export const messageImageValidation = () =>
  z
    .custom<File[] | string>()
    .refine(
      (file) =>
        typeof file === "string" ||
        (Array.isArray(file) && IMAGE_TYPES.includes(file[0].type)),
      {
        message: ".jpgもしくは.pngもしくはgifのみ利用可能です。",
      },
    )
    .transform(async (file) => {
      if (Array.isArray(file)) {
        return await encodeImageToBase64(file[0]);
      }
      return file;
    });

const PDF_TYPE = "application/pdf";
const MAX_PDF_SIZE = 100 * 1024 * 1024; // 100MB

export const messageAttachmentValidation = () =>
  z
    .custom<File[] | string>()
    .refine(
      (file) =>
        typeof file === "string" ||
        (Array.isArray(file) && file[0]?.type === PDF_TYPE),
      {
        message: ".pdfのみ利用可能です。",
      },
    )
    .refine(
      (file) =>
        typeof file === "string" ||
        !Array.isArray(file) ||
        file[0]?.size <= MAX_PDF_SIZE,
      {
        message: `PDFファイルのサイズが大きすぎます。${MAX_PDF_SIZE / 1024 / 1024}MB以下のファイルを選択するか、ファイルを分割して送信してください。`,
      },
    )
    .transform(async (file) => {
      if (Array.isArray(file)) {
        return await encodeImageToBase64(file[0]);
      }
      return file;
    });

export const dateValidation = () =>
  z.date({
    message: "日付を入力してください。",
  });

// TODO:画像バリデーション（png,jpeg,gif）
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/heic"];
export const imageValidation = () =>
  z
    .custom<File | File[] | string | null>()
    .refine(
      (file) =>
        // nullを許可（既存画像がある場合のため）
        file === null ||
        (typeof file === "string" && file.length > 0) ||
        file instanceof File ||
        (Array.isArray(file) && file.length > 0),
      { message: "画像をアップロードしてください。" },
    )
    .refine(
      (file) =>
        // nullを許可（既存画像がある場合のため）
        file === null ||
        typeof file === "string" ||
        (file instanceof File && IMAGE_TYPES.includes(file.type)) ||
        (Array.isArray(file) && IMAGE_TYPES.includes(file[0]?.type)),
      { message: ".jpg,.png,.heic,.gifのみ利用可能です。" },
    )
    .transform(async (file) => {
      if (file instanceof File) {
        const image = await encodeImageToBase64(file);
        return image;
      }
      if (Array.isArray(file) && file.length > 0) {
        const image = await encodeImageToBase64(file[0]);
        return image;
      }
      return file;
    });

// 金融機関コード
export const bankcodeValidation = () =>
  requiredValidation(i18n("bankcode"))
    .min(4, "4桁の半角数字を入力してください。")
    .regex(/^\d{4}$/, "4桁の半角数字を入力してください。");

// 支店コード
export const branchcodeValidation = () =>
  requiredValidation(i18n("branchcode")).regex(
    /^\d{3}$/,
    "3桁の半角数字を入力してください。",
  );

export const bankNameValidation = () => requiredValidation(i18n("bankName"));

export const branchNameValidation = () =>
  requiredValidation(i18n("branchName"));

export const accountTypeValidation = () =>
  z.union([z.literal("checking"), z.literal("ordinary")], {
    error: (issue) => {
      if (issue.code === "invalid_union") {
        return {
          message: "口座種別は普通、当座、その他のいずれかを入力してください。",
        };
      }
      return {
        message:
          issue.message ??
          "口座種別は普通、当座、その他のいずれかを入力してください。",
      };
    },
  });

export const accountNumberValidation = () =>
  requiredValidation(i18n("bankAccountNumber")).regex(sevenDigitPattern, {
    message: "7桁の半角数字を入力してください。",
  });

// TODO:口座名義人※カタカナのみ
export const accountHolderValidation = () =>
  requiredValidation(i18n("bankAccountHolder"))
    .regex(fullwidthCaracterPattern, {
      message: errorMessage("onlyFullwidthCaracters"),
    })
    .regex(/^[ァ-ヶー]+$/, {
      message: "カタカナで入力してください。",
    });

export const birthdayValidation = () =>
  z.coerce.date({
    message: `${i18n("birthday")}を入力してください。`,
  });

export const changeJobsValidation = () =>
  requiredValidation(i18n("changeJobs"));

export const relationshipValidation = () =>
  requiredValidation(i18n("relationship"));

export const occupationsValidation = () =>
  requiredValidation(i18n("occupation"));

export const breakTimesValidation = () =>
  z.array(
    z.object({
      startBreakTime: z.string().optional().nullable(),
      endBreakTime: z.string().optional().nullable(),
    }),
  );

export const startWorkDayValidation = () =>
  z.date({ message: `${i18n("startWorkDay")}を入力してください。` });

export const endWorkDayValidation = () =>
  z.date({ message: `${i18n("endWorkDay")}を入力してください。` });

export const startWorkTimeValidation = () =>
  requiredValidation(i18n("startWorkTime"));

export const endWorkTimeValidation = () =>
  requiredValidation(i18n("endWorkTime"));

export const startBreakDayValidation = () =>
  z.date({ message: `${i18n("startBreakDay")}を入力してください。` });

export const endBreakDayValidation = () =>
  z.date({ message: `${i18n("endBreakDay")}を入力してください。` });

export const fixRequestReasonValidation = () =>
  requiredValidation(i18n("fixRequestReason"));

export const isFacilityReasonValidation = () => z.boolean();

export const cancelConfilmValidation = () =>
  z
    .array(
      z.string({
        message: `${i18n("cancelPolicy")}を確認してください。`,
      }),
    )
    .nonempty(`${i18n("cancelPolicy")}を確認してください。`);

export const cancelReasonValidation = () =>
  requiredValidation(i18n("cancelReason"));

export const cancelMessageValidation = () =>
  requiredValidation(i18n("cancelMessage"));

export const desiredWithinRangeValidation = () =>
  z.string().min(1, `${i18n("desiredWithinRange")}を入力してください。`);

export const desiredTimeSlotValidation = () =>
  z.string().min(1, `${i18n("desiredTimeSlot")}を入力してください。`);

export const motivationOfJobChangeValidation = () =>
  z.string().min(1, `${i18n("motivationOfJobChange")}を入力してください。`);
